import { getFieldCategory } from './fieldCategoryMapping';
import { FIELD_CATEGORIES } from './fieldCategoryUtils';
import { logger } from './logger';

// Phone validation: allow optional leading + and 7-15 digits (ignoring spaces/dashes/parentheses)
const isValidPhone = (v) => {
  if (!v || typeof v !== 'string') return false;
  const digits = (v.match(/\d/g) || []).length;
  if (digits < 7 || digits > 15) return false;
  const pattern = /^\+?[0-9\s\-\(\)]{7,20}$/;
  return pattern.test(v);
};

/**
 * Maps employee data to workflow fields based on field names and categories.
 * Skips document/file fields and only returns simple values to be placed into additionalInfo.
 *
 * @param {Object} employeeData - The employee data object
 * @param {Array} workflowFields - Array of workflow field objects
 * @returns {Object} - Mapped field values keyed by field name
 */
export const mapEmployeeDataToWorkflowFields = (employeeData, workflowFields) => {
  if (!employeeData || !workflowFields || !Array.isArray(workflowFields)) {
    logger?.warn?.('Invalid inputs for employee data mapping', {
      hasEmployeeData: !!employeeData,
      hasWorkflowFields: !!workflowFields,
      isWorkflowFieldsArray: Array.isArray(workflowFields),
    });
    return {};
  }

  logger?.debug?.('Mapping employee data to workflow fields', {
    employeeDataKeys: Object.keys(employeeData || {}),
    workflowFieldCount: workflowFields.length,
  });

  const mappedValues = {};

  // Common synonyms/variations that may appear in workflow field names/titles/codes
  const fieldMappings = {
    // Name fields
    firstName: ['firstname', 'first_name', 'firstnameen', 'first_name_en'],
    middleName: ['middlename', 'middle_name', 'middlenameen', 'middle_name_en'],
    lastName: ['lastname', 'last_name', 'lastnameen', 'last_name_en'],
    fullName: ['fullname', 'full_name', 'name', 'nameen', 'name_en', 'fullnameen', 'employee_name', 'employeeName'],

    // Contact fields
    email: ['email', 'emailaddress', 'email_address'],
    phone: ['phone', 'phonenumber', 'phone_number', 'mobile', 'mobilenumber', 'mobile_number'],

    // Identity fields
    employeeId: ['employeeid', 'employee_id', 'id'],
    employeeUid: ['employeeuid', 'employee_uid', 'uid'],

    // Employment fields
    department: ['department', 'dept'],
    position: ['position', 'title', 'jobtitle', 'job_title', 'designation'],

    // Personal fields
    gender: ['gender', 'sex'],
    area: ['area'],
    nationality: ['nationality', 'country'],
    dateOfBirth: ['dateofbirth', 'dob', 'birthdate', 'birth_date'],

    // Passport fields (text/date fields, not file uploads)
    passportNumber: ['passportnumber', 'passport_no', 'passport_no.', 'passport-no', 'passportno'],
    passportIssueDate: ['passportissuedate', 'passport_issue_date', 'passportissued', 'passport_issue'],
    passportExpiry: ['passportexpiry', 'passport_expiry', 'passport_expiry_date', 'passportexpirydate'],

    // Emirates ID fields
    emiratesId: ['emiratesid', 'eid', 'idcard', 'id_card', 'emirates_id'],
    emiratesIdIssueDate: ['emiratesidissuedate', 'eid_issue_date', 'emirates_id_issue_date'],
    emiratesIdExpiryDate: ['emiratesidexpirydate', 'eid_expiry_date', 'emirates_id_expiry_date'],

    // Visa fields
    visaNumber: ['visanumber', 'visa_no', 'visa-no', 'visano'],
    visaType: ['visatype', 'visa_type'],
    visaIssueDate: ['visaissuedate', 'visa_issue_date'],
    visaExpiryDate: ['visaexpirydate', 'visa_expiry_date'],
    visaSponsorName: ['visasponsor', 'visa_sponsor_name', 'sponsorname', 'sponsor_name']
  };

  // Normalize a set of candidate values from employee data
  const candidates = {
    firstName:
      employeeData.firstNameEn || employeeData.firstName || null,
    middleName:
      employeeData.middleNameEn || employeeData.middleName || null,
    lastName:
      employeeData.lastNameEn || employeeData.lastName || null,
    fullName:
      employeeData.nameEn || employeeData.fullName || null,

    email: employeeData.email || null,
    phone:
      employeeData.mobileNumber || employeeData.phoneNumber || employeeData.phone || null,

    employeeId: employeeData.employeeId || employeeData.id || null,
    employeeUid: employeeData.uid || null,

    department: employeeData.department?.departmentName || null,
    position: employeeData.position?.positionName || null,

    gender: employeeData.gender || null,
    area: employeeData.area || null,
    nationality: employeeData.nationality || null,
    dateOfBirth: employeeData.dateOfBirth || employeeData.birthDate || null,

    // Passport
    passportNumber: employeeData.passportNumber || null,
    passportIssueDate: employeeData.passportIssueDate || null,
    passportExpiry: employeeData.passportExpiry || null,

    // Emirates ID
    emiratesId: employeeData.emiratesId || null,
    emiratesIdIssueDate: employeeData.emiratesIdIssueDate || null,
    emiratesIdExpiryDate: employeeData.emiratesIdExpiryDate || null,

    // Visa
    visaNumber: employeeData.visaNumber || null,
    visaType: employeeData.visaType || null,
    visaIssueDate: employeeData.visaIssueDate || null,
    visaExpiryDate: employeeData.visaExpiryDate || null,
    visaSponsorName: employeeData.visaSponsorName || null,
  };

  // Treat as document/file ONLY when explicit type/category hints say so.
  // Do NOT infer from name keywords, otherwise text fields like passportNumber/emiratesId get skipped.
  const isDocumentField = (field) => {
    const typeHints = [field?.type, field?.fieldType, field?.inputType, field?.uiType]
      .filter(Boolean)
      .map((t) => t.toString().toUpperCase());
    const category = (field?.category || '').toLowerCase();

    const hasFileLikeType = typeHints.some((t) => (
      t === 'FILE' || t === 'DOCUMENT' || t === 'PDF' || t === 'IMAGE'
    ));
    const hasFileLikeCategory = category.includes('document') || category.includes('file');
    const hasFileProps = !!(field?.accept || field?.allowedTypes || field?.acceptedTypes || field?.maxFileSize || field?.fileExtensions);

    return hasFileLikeType || hasFileLikeCategory || hasFileProps;
  };

  // Normalize date to YYYY-MM-DD if possible
  const normalizeDate = (v) => {
    if (!v) return v;
    try {
      // If already in YYYY-MM-DD format, return as-is
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      return v;
    }
  };

  workflowFields.forEach((field) => {
    if (!field || !field.name) return;

    // Skip documents
    if (isDocumentField(field)) return;

    const fieldName = field.name;
    const fieldNameLc = fieldName.toLowerCase();
    const fieldTitleLc = (field.title || '').toLowerCase();
    const fieldCodeLc = (field.code || '').toLowerCase();
    let value = null;
    let matched = false;

    // 1) Try explicit mapping rules first
    Object.entries(fieldMappings).some(([candidateKey, synonyms]) => {
      if (matched) return true;
      if (synonyms.some((s) => fieldNameLc.includes(s) || fieldTitleLc.includes(s) || fieldCodeLc.includes(s))) {
        value = candidates[candidateKey] ?? null;
       
        // Validate phone when mapping explicitly
        if (candidateKey === 'phone' && value && !isValidPhone(value)) {
          value = null;
        }
        // Normalize date-like values for known date keys
        if (value && [
          'dateOfBirth',
          'passportIssueDate', 'passportExpiry',
          'emiratesIdIssueDate', 'emiratesIdExpiryDate',
          'visaIssueDate', 'visaExpiryDate'
        ].includes(candidateKey)) {
          value = normalizeDate(value);
        }
        return true;
      }
      return matched;
    });
   
    // 2) If no match yet, try category-based mapping
    if (!matched) {
      const cat = getFieldCategory(field.name);
      switch (cat) {
        case FIELD_CATEGORIES.PERSONAL:
          value =
            employeeData[fieldNameLc] ??
            employeeData[fieldTitleLc] ??
            employeeData[fieldCodeLc] ??
            null;
          if (value && (fieldNameLc.includes('date') || fieldTitleLc.includes('date'))) {
            value = normalizeDate(value);
          }
          matched = value !== null && value !== undefined && value !== '';
          break;
        case FIELD_CATEGORIES.CONTACT:
          if ((fieldNameLc.includes('email') || fieldTitleLc.includes('email')) && candidates.email) {
            value = candidates.email;
            matched = true;
          } else if ((fieldNameLc.includes('phone') || fieldNameLc.includes('mobile')) && candidates.phone) {
            if (isValidPhone(candidates.phone)) {
              value = candidates.phone;
              matched = true;
            } else {
              value = null;
              matched = false;
            }
          }
          break;
        case FIELD_CATEGORIES.EMPLOYEE:
        case FIELD_CATEGORIES.EMPLOYEE_INFO:
          if ((fieldNameLc.includes('department') || fieldTitleLc.includes('department')) && candidates.department) {
            value = candidates.department;
            matched = true;
          } else if ((fieldNameLc.includes('position') || fieldNameLc.includes('job') || fieldNameLc.includes('title') || fieldNameLc.includes('designation')) && candidates.position) {
            value = candidates.position;
            matched = true;
          } else if ((fieldNameLc.includes('id') && !fieldNameLc.includes('uid')) && candidates.employeeId) {
            value = candidates.employeeId;
            matched = true;
          } else if (fieldNameLc.includes('uid') && candidates.employeeUid) {
            value = candidates.employeeUid;
            matched = true;
          } else if ((fieldNameLc.includes('passport') && fieldNameLc.includes('number')) && candidates.passportNumber) {
            value = candidates.passportNumber;
            matched = true;
          } else if ((fieldNameLc.includes('passport') && fieldNameLc.includes('issue')) && candidates.passportIssueDate) {
            value = normalizeDate(candidates.passportIssueDate);
            matched = true;
          } else if ((fieldNameLc.includes('passport') && (fieldNameLc.includes('expiry') || fieldNameLc.includes('expire'))) && candidates.passportExpiry) {
            value = normalizeDate(candidates.passportExpiry);
            matched = true;
          } else if ((fieldNameLc.includes('emirates') && fieldNameLc.includes('id')) && candidates.emiratesId) {
            value = candidates.emiratesId;
            matched = true;
          } else if ((fieldNameLc.includes('emirates') && fieldNameLc.includes('issue')) && candidates.emiratesIdIssueDate) {
            value = normalizeDate(candidates.emiratesIdIssueDate);
            matched = true;
          } else if ((fieldNameLc.includes('emirates') && (fieldNameLc.includes('expiry') || fieldNameLc.includes('expire'))) && candidates.emiratesIdExpiryDate) {
            value = normalizeDate(candidates.emiratesIdExpiryDate);
            matched = true;
          } else if ((fieldNameLc.includes('visa') && fieldNameLc.includes('number')) && candidates.visaNumber) {
            value = candidates.visaNumber;
            matched = true;
          } else if ((fieldNameLc.includes('visa') && fieldNameLc.includes('type')) && candidates.visaType) {
            value = candidates.visaType;
            matched = true;
          } else if ((fieldNameLc.includes('visa') && fieldNameLc.includes('issue')) && candidates.visaIssueDate) {
            value = normalizeDate(candidates.visaIssueDate);
            matched = true;
          } else if ((fieldNameLc.includes('visa') && (fieldNameLc.includes('expiry') || fieldNameLc.includes('expire'))) && candidates.visaExpiryDate) {
            value = normalizeDate(candidates.visaExpiryDate);
            matched = true;
          } else if ((fieldNameLc.includes('visa') && (fieldNameLc.includes('sponsor') || fieldNameLc.includes('sponsorname'))) && candidates.visaSponsorName) {
            value = candidates.visaSponsorName;
            matched = true;
          }
          break;
        default:
          break;
      }
    }

    if (matched) {
      const keyName = field.name;
      const keyCode = field.code;
      // Populate by code as well when components bind via field.code
      if (keyCode && keyCode !== keyName) {
        mappedValues[keyCode] = value;
      }
      mappedValues[keyName] = value;
    }
  });

  logger?.debug?.('Employee data mapping complete', {
    mappedFieldCount: Object.keys(mappedValues).length,
    mappedFields: Object.keys(mappedValues),
  });

  return mappedValues;
};

/**
 * Select default employee documents per document type and group to build a `documentSelections` array
 * suitable for workflow request payloads.
 *
 * Heuristics (from Workflow_DocumentGroupId_Integration_Plan.md):
 * - Prefer current group (isCurrent: true); fall back to the most recent group if none current.
 * - Within a group, prefer isPrimary: true; otherwise highest version.
 * - Include minimal snapshot metadata: id, filePath, fileName, contentType, version, isPrimary.
 *
 * @param {Array} groupedDocs - Array of grouped entries as returned by GET /v1/employee-documents/{uid}/grouped
 * @param {Object} options - Optional behavior overrides
 * @param {Record<string, boolean>} options.allowMultipleByType - Map of documentType => allow multiple selection (default false)
 * @returns {Array} documentSelections
 */
export const selectDefaultDocumentsForWorkflow = (groupedDocs, options = {}) => {
  try {
    const allowMultipleByType = options.allowMultipleByType || {};
    if (!Array.isArray(groupedDocs)) {
      logger?.warn?.('selectDefaultDocumentsForWorkflow: groupedDocs is not an array');
      return [];
    }

    // Normalize input into structure by documentType => [groups]
    const byType = groupedDocs.reduce((acc, entry) => {
      // Flexible property access per backend variations
      const documentType = (entry?.documentType || entry?.common?.documentType || entry?.type || '').toString().toLowerCase();
      const groupId = entry?.documentGroupId || entry?.groupId || entry?.group?.id;
      const common = entry?.common || {};
      const isCurrent = !!(entry?.isCurrent ?? common?.isCurrent);
      const documents = Array.isArray(entry?.documents) ? entry.documents : [];
      if (!documentType) return acc;
      const normalized = {
        documentType,
        documentGroupId: groupId,
        isCurrent,
        common,
        documents: documents.map((d) => ({
          id: d?.id || d?.employeeDocumentId || d?.docId,
          filePath: d?.filePath || d?.path || null,
          fileName: d?.fileName || d?.name || null,
          contentType: d?.contentType || d?.mimeType || null,
          version: typeof d?.version === 'number' ? d.version : (parseInt(d?.version, 10) || 0),
          isPrimary: !!(d?.isPrimary),
          uploadDate: d?.uploadDate || d?.createdAt || d?.createdDate || null,
        }))
      };
      if (!acc[documentType]) acc[documentType] = [];
      acc[documentType].push(normalized);
      return acc;
    }, {});

    const selections = [];

    Object.entries(byType).forEach(([docType, groups]) => {
      if (!groups.length) return;

      // Prefer current group; else pick the one with latest uploadDate across its documents
      let chosenGroup = groups.find((g) => g.isCurrent) || null;
      if (!chosenGroup) {
        chosenGroup = groups
          .map((g) => ({
            g,
            latestTs: Math.max(
              0,
              ...g.documents
                .map((d) => (d.uploadDate ? new Date(d.uploadDate).getTime() : 0))
            ),
          }))
          .sort((a, b) => b.latestTs - a.latestTs)[0]?.g || groups[0];
      }

      if (!chosenGroup || !Array.isArray(chosenGroup.documents) || chosenGroup.documents.length === 0) return;

      // Within the group, prefer isPrimary then highest version
      const allowMultiple = !!allowMultipleByType[docType];
      let selectedDocuments = [];
      if (allowMultiple) {
        // Select all docs in the chosen group, ordered by (isPrimary desc, version desc)
        selectedDocuments = [...chosenGroup.documents]
          .sort((a, b) => (b.isPrimary - a.isPrimary) || (b.version - a.version))
          .map(pickDocSnapshot);
      } else {
        const best = [...chosenGroup.documents]
          .sort((a, b) => (b.isPrimary - a.isPrimary) || (b.version - a.version))[0];
        if (best) selectedDocuments = [pickDocSnapshot(best)];
      }

      selections.push({
        documentType: docType,
        documentGroupId: chosenGroup.documentGroupId,
        selectedDocuments,
        common: pickCommonSnapshot(chosenGroup.common),
      });
    });

    logger?.debug?.('selectDefaultDocumentsForWorkflow: built selections', {
      typeCount: Object.keys(byType).length,
      selectionCount: selections.length,
    });
    return selections;
  } catch (e) {
    logger?.error?.('selectDefaultDocumentsForWorkflow failed', e);
    return [];
  }
};

// Helper: pick minimal common snapshot
const pickCommonSnapshot = (common) => {
  if (!common || typeof common !== 'object') return {};
  const issueDate = common.issueDate || common.documentIssueDate || null;
  const expiryDate = common.expiryDate || common.documentExpiryDate || null;
  const documentNumber = common.documentNumber || common.number || common.docNumber || null;
  return {
    issueDate: issueDate || null,
    expiryDate: expiryDate || null,
    documentNumber: documentNumber || null,
    isCurrent: !!common.isCurrent,
  };
};

// Helper: pick minimal document snapshot
const pickDocSnapshot = (d) => ({
  id: d?.id || null,
  filePath: d?.filePath || null,
  fileName: d?.fileName || null,
  contentType: d?.contentType || null,
  version: typeof d?.version === 'number' ? d.version : (parseInt(d?.version, 10) || 0),
  isPrimary: !!d?.isPrimary,
});

